<template>
    <section class="relative overflow-hidden">
        <div
            class="absolute -left-1/3 top-1/2 size-1/3 -translate-y-1/2 rounded-full bg-accent/40 blur-[9rem] delay-700" />
        <div
            class="absolute right-0 top-1/2 size-1/3 -translate-y-1/2 rounded-full bg-accent/40 blur-[9rem] delay-300" />

        <div class="container relative">
            <AtomTitle>Галерея</AtomTitle>
            <div
                class="relative mt-5 flex items-stretch max-lg:flex-col md:mt-10 md:max-lg:space-y-2 lg:space-x-5">
                <DirectionVideoCard
                    size="big"
                    :in-column="videos[config.public.website].length - 1"
                    :name="videos[config.public.website][0]['name']"
                    :title="videos[config.public.website][0]['title']"
                    :description="
                        videos[config.public.website][0]['description']
                    "
                    :image="videos[config.public.website][0]['image']"
                    :image-webp="videos[config.public.website][0]['image_webp']"
                    :duration="videos[config.public.website][0]['duration']"
                    :url="videos[config.public.website][0]['url']"
                    class="max-md:hidden" />
                <div class="relative h-auto w-full max-md:hidden">
                    <div
                        class="left-0 top-0 z-10 flex h-full w-full justify-between overflow-y-auto transition-all duration-300 max-lg:mt-5 max-md:flex-col max-md:space-y-5 md:space-x-5 lg:absolute lg:block lg:space-x-0 lg:space-y-3"
                        @scroll="handleScroll">
                        <DirectionVideoCard
                            v-for="(video, index) in videos[
                                config.public.website
                            ].slice(1)"
                            :key="index"
                            :name="video.name"
                            :title="video.title"
                            :description="video.description"
                            :image="video.image"
                            :image-webp="video.image_webp"
                            :in-column="
                                videos[config.public.website].length - 1
                            "
                            :duration="video.duration"
                            :url="video.url"
                            :class="{
                                'md:max-lg:shrink-0 md:max-lg:basis-1/3':
                                    videos[config.public.website].length - 1 >
                                    3,
                            }" />
                        <div
                            :style="{
                                '--tw-gradient-to-position': `${edgePosition}%`,
                            }"
                            :class="{
                                hidden:
                                    videos[config.public.website].length - 1 <=
                                    3,
                            }"
                            class="sticky bottom-0 left-0 z-30 h-[64px] w-full bg-gradient-to-t from-black to-95% max-lg:hidden" />
                    </div>
                </div>
                <div class="md:hidden">
                    <div class="swiper swiper-gallery">
                        <div class="swiper-wrapper">
                            <div
                                v-for="(video, index) in videos[
                                    config.public.website
                                ]"
                                :key="index"
                                class="swiper-slide !h-[16.18rem] !w-[17.5rem]">
                                <div class="swiper-slide__inner">
                                    <DirectionVideoCard
                                        size="medium"
                                        title-size="medium"
                                        :name="video.name"
                                        :title="video.title"
                                        :description="video.description"
                                        :image="video.image"
                                        :image-webp="video.image_webp"
                                        :duration="video.duration"
                                        :url="video.url"
                                        class="h-[16.18rem]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-10 text-center md:mt-20">
                <BaseLink
                    to="https://rutube.ru/channel/25014836/"
                    icon-left="rutube"
                    size="accent"
                    target="_blank"
                    icon-size="big">
                    Наш Rutube
                </BaseLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { Swiper } from 'swiper'
import type { VideoItem } from '~/types/global.types'

import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseLink from '~/components/base/base-link/BaseLink.vue'
import DirectionVideoCard from '~/components/pages/directions/direction-gallery-section/DirectionVideoCard.vue'

const config = useRuntimeConfig()
const edgePosition = ref<number>(100)

const videos: { [key: string]: VideoItem[] } = {
    'cromi.ru': [
        {
            name: 'Про CROMI',
            title: 'Короткий шоурил о нас',
            description: 'Офисы, склады и основные направления деятельности',
            image: '/assets/images/office/jpg-png/spb-1.jpg',
            image_webp: '/assets/images/office/webp/spb-1.webp',
            duration: '2:36',
            url: 'https://rutube.ru/video/dca3d0d2dff26bf08eb84b57120a4a8e/',
        },
        {
            name: 'Про электронику',
            title: 'Производство аудиогидов и пультов',
            description:
                'Полный серийный цикл: проектирование, ПО, автоматическая и ручная сборка, ОТК, дистрибуция',
            image: '/assets/images/usage/cromi/jpg-png/electronic.png',
            image_webp: '/assets/images/usage/cromi/webp/electronic.webp',
            duration: '1:14',
            url: 'https://rutube.ru/video/f7ff256088e03970146bbde867174964/',
        },
        {
            name: 'Про кабины',
            title: 'Производство кабин переводчиков',
            description:
                'Полный серийный цикл: проектирование, отливка профиля и пластиковых деталей, ЧПУ, сборка, ОТК, дистрибуция',
            image: '/assets/images/usage/cromi/jpg-png/cabin-modeling-2.jpg',
            image_webp: '/assets/images/usage/cromi/webp/cabin-modeling-2.webp',
            duration: '1:27',
            url: 'https://rutube.ru/video/e94442f5fffd6a1d81d7997742838342/',
        },
    ],
    'cromi.rent': [
        {
            name: 'Как проходит мероприятие',
            title: 'Процесс на площадке',
            description:
                'Завоз всего оборудования, монтаж кабины переводчиков, быстрая установка и настройка всех систем, успешное сопровождение и оперативный демонтаж',
            image: '/assets/images/usage/video/jpg-png/video-recording.png',
            image_webp: '/assets/images/usage/video/webp/video-recording.webp',
            duration: '1:53',
            url: 'https://rutube.ru/video/73d10bcde26b9a3ba106c58fa37cecc5/',
        },
        {
            name: 'Про оборудование',
            title: 'Что на складе?',
            description:
                'Как мы бережно обслуживаем, проверяем и отгружаем оборудование перед каждым мероприятием',
            image: '/assets/images/usage/cromi/jpg-png/cromi-warehouse.png',
            image_webp: '/assets/images/usage/cromi/webp/cromi-warehouse.webp',
            duration: '1:22',
            url: 'https://rutube.ru/video/4452b7c194c4870eb40da01ccd022d13/',
        },
        {
            name: 'Про людей',
            title: 'Что в офисе?',
            description:
                'Какой путь заявка на аренду оборудования проходит от звонка до готового проекта для реализации',
            image: '/assets/images/office/jpg-png/spb-5.png',
            image_webp: '/assets/images/office/webp/spb-5.webp',
            duration: '1:01',
            url: 'https://rutube.ru/video/2d9c6c63eed13140779a73998b1cdaa5/',
        },
    ],
    'radioguide.rent': [
        {
            name: 'Про аренду радиогидов',
            title: 'Как это происходит',
            description:
                'Кратко полный цикл аренды радиогидов для экскурсий: подготовка, доставка, возврат и техподдержка',
            image: '/assets/images/products/radioguide/jpg-png/transmitter-t-100_radioguide.png',
            image_webp:
                '/assets/images/products/radioguide/webp/transmitter-t-100_radioguide.webp',
            duration: '1:24',
            url: 'https://rutube.ru/video/929b51c79f914ec7c48adb74414a7621/',
        },
        {
            name: 'Как в работе',
            title: 'Как используется?',
            description: 'В разных локациях: музей, улица, теплоход, завод',
            image: '/assets/images/usage/radioguide/jpg-png/boat-group-tour-2.jpg',
            image_webp:
                '/assets/images/usage/radioguide/webp/boat-group-tour-2.webp',
            duration: '0:30',
            url: 'https://rutube.ru/video/255b7f7e5b1a9dad1b4ab5a40fa83a43/',
        },
        {
            name: 'Про людей',
            title: 'Что в офисе?',
            description:
                'Какой путь заявка на аренду оборудования проходит от звонка до готового проекта для реализации',
            image: '/assets/images/office/jpg-png/spb-5_radioguide.png',
            image_webp: '/assets/images/office/webp/spb-5_radioguide.webp',
            duration: '1:01',
            url: 'https://rutube.ru/video/2d9c6c63eed13140779a73998b1cdaa5/',
        },
        {
            name: 'Про оборудование',
            title: 'С чем работаем?',
            description:
                'Короткий шоурил радиогидов Reinvox, которые мы используем в аренде',
            image: '/assets/images/usage/cromi/jpg-png/radioguides-checking_radioguide.png',
            image_webp:
                '/assets/images/usage/cromi/webp/radioguides-checking_radioguide.webp',
            duration: '0:31',
            url: 'https://rutube.ru/video/dbf1212a7609a21f4f85b4122d69130c/',
        },
    ],
}

function handleScroll(event: Event): void {
    if (!event.target || !(event.target instanceof Element)) return

    const scrollHeight: number =
        event.target.scrollHeight - event.target.clientHeight - 64
    const scrollPercentage: number = Number(
        ((event.target.scrollTop / scrollHeight) * 100).toFixed(2)
    )

    edgePosition.value = 100 - scrollPercentage
}

onMounted(() => {
    new Swiper('.swiper-gallery', {
        slidesPerView: 'auto',
        spaceBetween: 8,
        breakpoints: {
            375: {
                spaceBetween: 20,
            },
        },
    })
})
</script>
